import { forwardRef, InputHTMLAttributes, useState } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { variant } from "styled-system";
import { useId } from "@react-aria/utils";

import Box from "../Box/Box";
import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";

import { InputProps, InputLabelProps, InputVariants } from "./input.type";
import variants, { iconVariants } from "./variants";

const StyledInput = styled.input<
  InputHTMLAttributes<HTMLInputElement> & {
    variant: InputVariants;
    css?: string;
    hasIcon?: boolean;
  }
>`
  box-sizing: border-box;
  height: 48px;
  width: 100%;
  padding: 0 14px;
  outline: none;

  ${(props) =>
    css({
      borderRadius: 2,
      fontSize: "body-medium",
      fontFamily: "normal",
      fontWeight: "normal",
      lineHeight: "140%",
      border: "2px solid",
      borderColor: "msg-blue-border",
      color: "primary-navy-blue",
      padding: props.hasIcon ? "0 14px 0 36px" : "0 14px",
      "&:focus, &:hover": {
        boxShadow:
          props.variant !== "default"
            ? `0px 1px 2px 2px rgba(21, 45, 109, 0.08),
             2px 0px 8px 4px rgba(161, 194, 218, 0.24),
             1px 1px 2px 2px rgba(21, 45, 109, 0.06),
             2px 2px 4px 2px rgba(161, 194, 218, 0.06),
             0px 2px 8px 2px rgba(161, 194, 218, 0.06)`
            : "none",
      },

      "&::placeholder": {
        color: "lightest-grey",
      },
    })};
  ${variant({ variants })}
  ${(props) => props.css}
`;

const InputContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const InputLabel = styled(Typography)<InputLabelProps>`
  color: #404040;
  height: 16px;
  ${(props) =>
    props.required
      ? css({
          "&::after": {
            content: "'*'",
            color: "primary-red",
          },
        })
      : null}
  ${css({
    fontSize: "body-small",
    fontWeight: "medium",
    marginY: 4,
  })}
`;

const InputIcon = styled.span`
  display: block;
  position: absolute;
  top: calc(50% - 7px);
  left: 14px;
  z-index: 1;
`;

const InputWrapper = styled(Box)`
  position: relative;
`;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      width,
      label,
      paddingX,
      required,
      icon,
      iconWeight = "regular",
      error = false,
      value,
      onChange,
      children,
      warning,
      success,
      focused,
      ...props
    }: InputProps,
    ref
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const uid = useId();
    const hasIcon = (icon && icon.length > 0) as boolean;

    const getVariant = () => {
      if (error) {
        return "error";
      }
      if (warning) {
        return "warning";
      }
      if (success) {
        return "success";
      }

      if (isFocused || focused) {
        return "focused";
      }

      return "default";
    };

    const iconVariant = iconVariants[getVariant()];

    return (
      <InputContainer paddingX={paddingX} width={width}>
        {label && (
          <InputLabel htmlFor={id || uid} as="label" required={required}>
            {label}
          </InputLabel>
        )}
        <InputWrapper>
          {icon && (
            <InputIcon>
              <Icon
                name={icon}
                width={14}
                height={14}
                weight={iconWeight}
                color={iconVariant.color}
              />
            </InputIcon>
          )}
          <StyledInput
            id={id || uid}
            variant={getVariant()}
            required={required}
            ref={ref}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label="input field"
            hasIcon={hasIcon}
            {...props}
          />
        </InputWrapper>
        {children}
      </InputContainer>
    );
  }
);

export default Input;
